import React, { useState, FC, useContext } from 'react';
import { PrimaryButton, useSnackbar, IOTTableWrapper, Card, DeleteDialog } from '@Iot-Bee/standard-web-library';

import CalibrationDialog from './components/CalibrationDialog';

import { noCacheFetcher } from '../../utils/fetch';
import { Calibration } from '../../Types/types';
import { useCustomSWR } from '../../hooks/customSWR';
import { columns } from './util';
import { DevicesContext } from '../../context/DevicesContext';

export const CalibrationPage: FC = () => {
  const devicesContext = useContext(DevicesContext);

  if (devicesContext === undefined) {
    throw new Error('DevicesContext is undefined');
  }

  const {
    data: calibrations,
    isLoading,
    mutate: refreshCalibrations,
  } = useCustomSWR<Calibration[]>(`${process.env.REACT_APP_API_URL}getcalibrations`);

  const [dialogInfo, setDialogInfo] = useState<{
    open: boolean;
    mode: 'add' | 'edit';
    calibration: Calibration | null;
  }>({
    open: false,
    mode: 'add',
    calibration: null,
  });

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; calibration: Calibration }>({
    open: false,
    calibration: {} as Calibration,
  });

  const {
    actions: { openSnackbar },
  } = useSnackbar();

  const handleSaveCalibration = (calibration: Calibration) => {
    // validate the calibration
    if (calibration.name === '') {
      openSnackbar('error', 'Please enter a name for the calibration');
      return;
    }
    if (calibration.value === '') {
      openSnackbar('error', 'Please enter a value for the calibration');
      return;
    }

    if (calibration.value !== null && !calibration.value.includes('$')) {
      openSnackbar('error', 'The calibration value must be in the format: $/400');
      return;
    }
    //TODO: this is not 100% correct as dollarsign can be put many places. You can try and look at my code in DeviceDataCard where i use the dollarsign
    const resultsInValue = calibration.value !== null ? calibration.value.replaceAll('$', '1') : '1';
    let valid = false;
    try {
      /*jslint evil: true */
      const res = Function('return ' + resultsInValue)();
      if (!isNaN(res)) {
        valid = true;
      }
    } catch (e) {
      console.error(e);
      valid = false;
    }

    if (!valid) {
      alert('The calibration does not return a number');
      return;
    }
    // This returns the id of the new calibration
    noCacheFetcher(`${process.env.REACT_APP_API_URL}addcalibration`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(calibration),
    })
      .then(() => {
        refreshCalibrations();
        setDialogInfo({ open: false, mode: 'add', calibration: null });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleDeleteCalibration = (calibration: Calibration) => {
    noCacheFetcher(`${process.env.REACT_APP_API_URL}deletecalibration`, {
      method: 'DELETE',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ id: calibration.id }),
    })
      .then(() => {
        refreshCalibrations();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleEditCalibration = (calibration: Calibration) => {
    noCacheFetcher(`${process.env.REACT_APP_API_URL}editcalibration`, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(calibration),
    })
      .then(() => {
        refreshCalibrations();
        setDialogInfo({ open: false, mode: 'add', calibration: null });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <Card
      title={'Calibration'}
      headerActions={
        <PrimaryButton
          onClick={() => {
            setDialogInfo({ open: true, mode: 'add', calibration: null });
          }}
        >
          Add new calibration
        </PrimaryButton>
      }
    >
      <IOTTableWrapper
        columns={columns}
        data={calibrations ?? []}
        onEdit={(itemToEdit) => {
          setDialogInfo({ open: true, mode: 'edit', calibration: itemToEdit });
        }}
        onDelete={(itemToDelete) => {
          setDeleteDialog({ open: true, calibration: itemToDelete });
        }}
        isLoading={isLoading}
      />
      {dialogInfo.open && (
        <CalibrationDialog
          calibration={dialogInfo.calibration}
          onClose={() => setDialogInfo({ open: false, mode: 'add', calibration: null })}
          onSave={dialogInfo.mode === 'add' ? handleSaveCalibration : handleEditCalibration}
        />
      )}
      <DeleteDialog
        title={deleteDialog.calibration?.name}
        open={deleteDialog.open}
        handleDelete={() => {
          handleDeleteCalibration(deleteDialog.calibration);
          setDeleteDialog((prev) => ({ ...prev, open: false }));
        }}
        onClose={() => setDeleteDialog((prev) => ({ ...prev, open: false }))}
        itemName={deleteDialog.calibration?.name}
      />
    </Card>
  );
};

export default CalibrationPage;
